import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { View, StyleSheet, type LayoutChangeEvent } from 'react-native';
import { useTranslation } from 'react-i18next';

import { LoggerDockBand } from './LoggerDockBand';

import type { useTheme } from './theme';

// LG-94 — the strip of screen the keyboard does NOT cover.
//
// The problem this exists for: `SetRowStepperBar` was rendered in the flow,
// under `weightFocused &&` — so it was born exactly where the keyboard covers
// it, and there was no state of the app in which an athlete could see it. The
// rest timer already lived in the right place, docked to the bottom edge, and
// `adjustResize` is set: a bottom-anchored View ends up ABOVE the keyboard by
// itself, with no Keyboard listener to get wrong (which matters, because
// `Keyboard.addListener` never fires for a hardware keyboard — see
// useFieldFocus.ts for the same reasoning).
//
// ⛔ WHY A REGISTRY AND NOT A LIFT. The obvious cure is to hand the dock the
// data and let it build the bars. It works while there is ONE bar. There are
// two (`FocusPanelInputs` for the weight cell, `FocusPanelTimed` for the hold
// extra), each with its own derivations — `loadAxisHasValue`, the step table,
// `applyDelta`, the plate-modal opener, and `prescribeLoad`, which is a prop
// and deliberately NOT a context field (`loggerContext.ts` calls adding one
// "a decision, not a formality"). Rebuilding all of that in the dock would put
// every visibility condition in two places able to disagree. So the panels
// keep building their own bar, exactly as today, and only hand it somewhere
// else to be DRAWN.
//
// ⛔ AND WHY IT DOES NOT LOOP. A registry of React nodes is one careless step
// from an infinite render: the child builds a new element every render, the
// effect writes it, the provider's state changes, the child renders again. It
// does not happen here, and the structure is the reason, not luck:
//   - the setter lives in its own context and is created ONCE, so a panel that
//     only registers never re-renders when the registry changes;
//   - `children` is the same element across a registry update, so React skips
//     that whole subtree — the outlet re-renders, the logger does not.
// ⚠️ Keep both properties if you touch this. A `useMemo` on the provider value
// that folds the slots in with the setter would quietly restore the loop.

type SlotKey = 'rest' | 'load';

/** Top to bottom. The rest timer keeps the higher line: it is the ambient
 *  clock, while the load controls belong under the thumb that is typing. */
const DOCK_ORDER: readonly SlotKey[] = ['rest', 'load'];

type SetSlot = (key: SlotKey, node: React.ReactNode) => void;

const SetSlotContext = createContext<SetSlot | null>(null);
const DockHeightContext = createContext(0);

/**
 * Hand a node to the dock. Pass `null` to occupy nothing — a slot with no node
 * takes no height, so the ordinary "this bar does not apply to this set" case
 * needs no special handling anywhere.
 *
 * @returns whether a dock actually took it. ⛔ NOT a convenience: the logger's
 * panels can be mounted where there is no screen bottom to dock to: an
 * absolute band inside a clipped or swiped container would be cut off. So a
 * panel with no dock above it renders its bar in place, and only the logger
 * gets the docked one. A false here means "draw it yourself", never
 * "something went wrong".
 */
export function useDockSlot(key: SlotKey, node: React.ReactNode): boolean {
  const setSlot = useContext(SetSlotContext);
  useEffect(() => {
    setSlot?.(key, node);
  }, [setSlot, key, node]);
  // Clearing on unmount is separate on purpose: a panel swapped out under the
  // athlete must not leave its bar docked over the next exercise.
  useEffect(
    () => () => {
      setSlot?.(key, null);
    },
    [setSlot, key],
  );
  return setSlot !== null;
}

/**
 * Hand the weight controls to the dock, dressed as a band.
 *
 * ⛔ It exists because BOTH load bars need the identical five lines — the focus
 * panel's weight cell and the hold/isometric extra — and the second copy went
 * in during the same changeset as the first. Two panels spelling the same
 * wiring is how the two bars start looking different: one gets a padding, the
 * other does not, and nobody notices because they are never on screen together.
 *
 * ⭐ The label lives here too, so no caller has to remember the key.
 *
 * @returns `inFlow` — what the caller must render itself, which is `null` when
 * a dock took the bar. It is NOT simply the bar: with no dock above (the
 * program editor's swiped week deck) the bar still gets the region role and
 * the same name, just without the band's surface.
 *
 * ⛔ The name is not optional off the dock. The a11y audit called the earlier
 * "the card already frames it, so it needs no name" an over-quick call: the
 * individual buttons are labelled either way, but the REGION a reduced-vision
 * athlete navigates by was there in the logger and missing in the editor, and
 * nothing the athlete can see explains the difference. The surface is what the
 * dock adds; the name belongs to the bar wherever it is.
 */
export function useDockedLoadBar(
  bar: React.ReactNode,
  theme: ReturnType<typeof useTheme>,
): { docked: boolean; inFlow: React.ReactNode } {
  const { t } = useTranslation();
  const label = t('mobile.logger.loadBarA11y', {
    defaultValue: 'Weight controls',
  });
  const docked = useDockSlot(
    'load',
    bar == null ? null : (
      <LoggerDockBand theme={theme} accessibilityLabel={label}>
        {bar}
      </LoggerDockBand>
    ),
  );
  return {
    docked,
    inFlow:
      docked || bar == null ? null : (
        <View accessibilityRole="toolbar" accessibilityLabel={label}>
          {bar}
        </View>
      ),
  };
}

/** The room the dock occupies, for the scroller to reserve. */
export function useDockHeight(): number {
  return useContext(DockHeightContext);
}

/** A spacer of exactly the dock's height. Put it last inside the scroller so
 *  the final card can still be scrolled clear of the bars. */
export function LoggerDockSpacer(): React.ReactElement {
  return <View style={{ height: useDockHeight() }} />;
}

type Slots = Partial<Record<SlotKey, React.ReactNode>>;

const SlotsContext = createContext<Slots>({});
const SetHeightContext = createContext<(h: number) => void>(() => {});

/** Context only — it renders no View, so the layout tree is unchanged and the
 *  children stay exactly the siblings they were. */
export function LoggerDockProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [slots, setSlots] = useState<Slots>({});
  const [height, setHeight] = useState(0);

  // ⛔ Both stable for the life of the logger — see the loop note above.
  const setSlot = useCallback<SetSlot>((key, node) => {
    setSlots((prev) => (prev[key] === node ? prev : { ...prev, [key]: node }));
  }, []);
  const reportHeight = useCallback((h: number) => {
    setHeight((prev) => (prev === h ? prev : h));
  }, []);

  return (
    <SetSlotContext.Provider value={setSlot}>
      <SetHeightContext.Provider value={reportHeight}>
        <SlotsContext.Provider value={slots}>
          <DockHeightContext.Provider value={height}>
            {children}
          </DockHeightContext.Provider>
        </SlotsContext.Provider>
      </SetHeightContext.Provider>
    </SetSlotContext.Provider>
  );
}

/**
 * The one absolutely-positioned band, mounted where the rest bar used to be.
 *
 * ⛔ Its PLACE among the siblings is load-bearing, not cosmetic: RN paints
 * later siblings on top, so an outlet rendered after the history sheet and the
 * commit prompts would draw the rest timer over a dialog. It goes immediately
 * after the scroller, which is exactly where the docked rest bar already sat.
 */
export function LoggerDockOutlet(): React.ReactElement | null {
  const slots = useContext(SlotsContext);
  const reportHeight = useContext(SetHeightContext);

  const filled = useMemo(
    () => DOCK_ORDER.filter((key) => slots[key] != null),
    [slots],
  );

  const onLayout = useCallback(
    (e: LayoutChangeEvent) => reportHeight(e.nativeEvent.layout.height),
    [reportHeight],
  );

  // ⛔ Release the reserved room on the way out. `onLayout` does not fire when
  // a View unmounts, so without this the page keeps a band of empty space for
  // bars that are gone, and the last card sits above a gap forever. (The rest
  // bar carried this same guard before the dock existed.)
  const empty = filled.length === 0;
  useEffect(() => {
    if (empty) reportHeight(0);
  }, [empty, reportHeight]);

  if (empty) return null;

  return (
    <View style={styles.dock} onLayout={onLayout}>
      {filled.map((key) => (
        <React.Fragment key={key}>{slots[key]}</React.Fragment>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  // No colour, no border, no padding: the dock is a position, and each band
  // brings its own surface. A frame here would draw a box around bands that
  // already have one.
  dock: { position: 'absolute', left: 0, right: 0, bottom: 0 },
});
